import { useCallback, useEffect, useState } from 'react'

import SafeJSON from 'modules/core/lib/SafeJSON'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'

interface StoredData<T> {
  data: T
  expiresAt: number
}

const readAndValidate = <T>(key: string): StoredData<T> | null => {
  try {
    const rawValue = SafeLocalStorage.getItem(key, null)
    if (!rawValue) return null

    const parsed = SafeJSON.parse<StoredData<T> | null>(rawValue, null)
    if (!parsed) return null

    // Check expiration
    if (Date.now() > parsed.expiresAt) {
      SafeLocalStorage.setItem(key, JSON.stringify(null))
      return null
    }

    return parsed
  } catch {
    return null
  }
}

export const useLocalStorageWithExpiration = <T>(
  key: string,
  expirationDays: number
): [T | null, (value: T) => void] => {
  const [storedValue, setStoredValue] = useState<StoredData<T> | null>(() =>
    readAndValidate<T>(key)
  )

  useEffect(() => {
    setStoredValue(readAndValidate<T>(key))
  }, [key])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== key) return

      if (e.newValue === null) {
        setStoredValue(null)
      } else {
        setStoredValue(readAndValidate<T>(key))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key])

  const setValue = useCallback(
    (value: T) => {
      // Skip if value hasn't changed
      if (storedValue && JSON.stringify(storedValue.data) === JSON.stringify(value)) {
        return
      }

      const item: StoredData<T> = {
        data: value,
        expiresAt: Date.now() + expirationDays * 24 * 60 * 60 * 1000,
      }

      SafeLocalStorage.setItem(key, JSON.stringify(item))
      setStoredValue(item)
    },
    [expirationDays, key, storedValue]
  )

  return [storedValue?.data ?? null, setValue]
}

export const readLatestUpsellOrigin = (): {
  request_source: string
  request_source_category?: string
  request_source_component?: string
} | null => {
  try {
    const rawValue = SafeLocalStorage.getItem('upsell_origin', null)
    if (!rawValue) return null

    const parsed = SafeJSON.parse<StoredData<{
      request_source: string
      request_source_category?: string
      request_source_component?: string
    }> | null>(rawValue, null)

    if (!parsed || !parsed.data) return null

    if (Date.now() > parsed.expiresAt) return null

    return parsed.data
  } catch {
    return null
  }
}
